import { useEffect } from 'react';
import { useTemplateContext } from '../context/TemplateContext';
import { fetchAllTemplates } from '../services/templateService';
import type { TemplateId, TemplateConfig } from '../types';

/**
 * Primary hook for template operations.
 * Loads templates on mount (once), exposes select/active/list.
 */
export function useTemplate() {
  const { state, activeTemplate, selectTemplate, loadTemplates, setError } =
    useTemplateContext();

  // Load templates once on mount
  useEffect(() => {
    if (state.templates.length > 0 || state.isLoading) return;

    fetchAllTemplates()
      .then((configs) => loadTemplates(configs))
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to load templates';
        setError(message);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    /** All available templates */
    templates: state.templates,

    /** Currently selected template config (null until loaded) */
    activeTemplate,

    /** ID of the active template */
    activeTemplateId: state.activeTemplateId,

    /** Select a template by ID */
    selectTemplate,

    /** True while templates are being loaded */
    isLoading: state.isLoading,

    /** Error message if load failed */
    error: state.error,
  };
}

/**
 * Returns a specific layer config from the active template by layer ID.
 * Useful for PropertyPanel layer-specific controls.
 */
export function useTemplateLayer(layerId: string) {
  const { activeTemplate } = useTemplateContext();
  return activeTemplate?.layers.find((l) => l.id === layerId) ?? null;
}

/**
 * Returns the active template's resolution as { width, height }.
 */
export function useTemplateResolution(): { width: number; height: number } | null {
  const { activeTemplate } = useTemplateContext();
  if (!activeTemplate) return null;
  const [w, h] = activeTemplate.metadata.resolution.split('x').map(Number);
  return { width: w, height: h };
}
